/**
 * The one emitter for "Kelly was shown / hidden / promoted". It watches the slice rather than living on the
 * controls, because there are five ways the view moves — a launcher pill in each of five shells, the dock
 * header's Close, Escape, the header's Expand, and the `/kelly-ai` bridge promoting and handing back — and a
 * per-control emitter would have to be added to each of them and to the next one somebody writes. Which
 * control did it is deliberately not reported: `route` already says which page Kelly was opened from, and
 * carrying the control as well cost a field in the slice that nothing but analytics read.
 *
 * Mounted once, by KellyAnalyticsProvider.
 */

import { useEffect, useRef } from "react"
import { useAppSelector } from "hooks/redux"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { selectDockView } from "../store/agent-chat-slice"
import { useKellyAnalytics } from "../chatbox/use-kelly-analytics"

export function useTrackDockView(): void {
  const dockView = useAppSelector(selectDockView)
  const analytics = useKellyAnalytics()

  // The view as of the last emit. Seeded from the first render, so mounting on a closed dock — every page
  // load — is not itself an event, and a deep link that arrives already `full` reports one open, not two.
  const emittedViewRef = useRef(dockView)
  // Reading `analytics` through a ref keeps this a dockView-only effect. It cannot be a dependency: the
  // surface properties it carries include `route`, so every navigation would re-run the effect, and a
  // navigation with Kelly open would re-emit an open on each one.
  const analyticsRef = useRef(analytics)
  useEffect(() => {
    analyticsRef.current = analytics
  }, [analytics])

  useEffect(() => {
    const previous = emittedViewRef.current
    if (previous === dockView) return
    emittedViewRef.current = dockView

    const wasOpen = previous !== "closed"
    const isOpen = dockView !== "closed"

    if (!wasOpen && isOpen) {
      analyticsRef.current.track(AI_TEAMMATE_EVENTS.DRAWER_OPENED)
      // A deep link opens straight into full screen. That is one open in one view, not an open plus a promote,
      // so the toggle event is deliberately not also emitted here.
      return
    }
    if (wasOpen && !isOpen) {
      analyticsRef.current.track(AI_TEAMMATE_EVENTS.DRAWER_CLOSED)
      return
    }
    // Both open: the only remaining move is docked <-> full.
    analyticsRef.current.track(AI_TEAMMATE_EVENTS.DOCK_FULLSCREEN_TOGGLED, { expanded: dockView === "full" })
  }, [dockView])
}
